import { useMemo } from "react";
import { Game } from "@onmo/graphql/graphql";
import { Dialog } from "../../core/Dialog/Dialog";
import { Image } from "../../core/Image/Image";
import { useStyles } from "./GameQueuePopup.style";
import { NavigationMd } from "../../../models/navigation";
import { useTranslation } from "react-i18next";

type IGameQrPopup = {
  open: boolean;
  type: "up" | "expire" | "leave";
  submitLoading?: boolean;
  game?: Game;
  onClose?: () => void;
  onSubmit?: () => void;
};

export const GameQueuePopup: React.FC<IGameQrPopup> = (props) => {
  const { onClose, open, type, submitLoading, game, onSubmit } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { src1x, src2x, src3x } = useMemo(() => {
    return NavigationMd.getContent(game);
  }, [game]);

  const getTitle = useMemo(() => {
    switch (type) {
      case "up":
        return t("Queue_You're Up!");
      case "expire":
        return t("Queue_Session Expired");
      case "leave":
        return t("Queue_Leave Current Queue?");
    }
  }, [type, t]);

  const getDescription = useMemo(() => {
    switch (type) {
      case "up":
        return t(
          "Queue_Your private smart console is ready to play! We’ll hold the slot for 5 minutes.",
        );
      case "expire":
        return t(
          "Queue_You didn't start your game in time. Your queue position has been released.",
        );
      case "leave":
        return t(
          "Queue_To start another game, you'll need to leave your current queue. Your position cannot be recovered.",
        );
    }
  }, [type, t]);

  const actions = useMemo(() => {
    switch (type) {
      case "up":
        return {
          mainAction: {
            text: t("Queue_Start Game"),
            onClick: onSubmit,
          },
          secondAction: {
            text: t("Queue_Abandon"),
            onClick: onClose,
          },
        };
      case "expire":
        return {
          mainAction: {
            text: t("Queue_Ok"),
            onClick: onClose,
          },
        };
      case "leave":
        return {
          mainAction: {
            text: t("Queue_Leave Queue"),
            onClick: onSubmit,
          },
          secondAction: {
            text: t("Queue_Cancel"),
            onClick: onClose,
          },
        };
    }
  }, [type, onSubmit, onClose, t]);

  return (
    <Dialog
      icon={
        <Image
          src={src1x}
          src2x={src2x}
          src3x={src3x}
          className={classes.gameImage}
        />
      }
      loadingText={submitLoading ? "Quitting" : undefined}
      open={open}
      title={getTitle}
      description={getDescription}
      {...actions}
    />
  );
};
